import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { PaginationProps } from './types';
import { WindowHelperWrapper } from '../../../utils';
import { BUTTON_BREAKPOINTS, DEFAULT_BUTTON_COUNTS, getPagingInfo } from './utils';
import { PaginationComponent } from './ui-components';

export const Pagination: FC<PaginationProps> = props => {
  const {
    activePage: defaultActivePage = 1,
    isBranded = false,
    isLoadingData,
    maxButtons,
    onPageChange,
    windowOverride,
    coreTable = false,
  } = props;

  const [activePage, setActivePage] = useState(defaultActivePage);
  const [defaultButtonCount, setDefaultButtonCount] = useState(9);
  const windowHelper = useMemo(() => WindowHelperWrapper(windowOverride), [windowOverride]);
  const pagingInfo = useMemo(() => getPagingInfo(props), [props]);

  const onButtonCountChange = useCallback(
    (index: number) => {
      setDefaultButtonCount(DEFAULT_BUTTON_COUNTS[index]);
    },
    [setDefaultButtonCount]
  );

  useEffect(() => {
    const destroyWindowWatcher = windowHelper.watchBreakpoints(BUTTON_BREAKPOINTS, onButtonCountChange);

    return () => {
      destroyWindowWatcher();
    };
  }, []);

  useEffect(() => {
    if (defaultActivePage !== activePage) {
      setActivePage(defaultActivePage);
    }
  }, [defaultActivePage, activePage]);

  const handleSelect = (newPage: number) => {
    setActivePage(newPage);
    onPageChange(newPage);
  };

  if (pagingInfo.dataSize !== pagingInfo.totalCount && (isLoadingData || !pagingInfo.hasPagination)) {
    return null;
  }

  const siblingPagesRange = Math.max(0, maxButtons ? (maxButtons - 3) / 2 : defaultButtonCount);
  const currentPage = activePage > pagingInfo.pageCount ? pagingInfo.pageCount : activePage;
  const displayedPage = coreTable && defaultActivePage ? defaultActivePage : currentPage;
  const meta = useMemo(
    () => ({
      ...pagingInfo,
      currentPage: displayedPage,
    }),
    [pagingInfo, displayedPage]
  );

  return (
    <PaginationComponent
      currentPage={displayedPage}
      hideFirstAndLastPageLinks={true}
      isBranded={isBranded}
      onChange={handleSelect}
      siblingPagesRange={siblingPagesRange}
      totalPages={pagingInfo.pageCount}
      coreTable={coreTable}
      meta={meta}
    />
  );
};
